import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";

const TYPES = [
  { value: "farmacia", label: "Farmácia" },
  { value: "profissional_saude", label: "Profissional de saúde" },
  { value: "cuidador", label: "Cuidador autônomo" },
  { value: "clinica", label: "Clínica" },
  { value: "outro", label: "Outro" },
];

export default function PartnerSignup() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ code: string } | null>(null);
  const [authedEmail, setAuthedEmail] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", partner_type: "outro", document: "", notes: "",
  });

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const email = data.user?.email ?? null;
      if (email) {
        setAuthedEmail(email);
        setForm((p) => ({ ...p, email }));
      }
    });
  }, []);

  const set = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("referral-partner-signup", { body: form });
      if (error || (data as any)?.error) {
        toast.error((data as any)?.error || error?.message || "Erro ao enviar.");
        return;
      }
      setDone({ code: (data as any).partner.code });
      toast.success("Cadastro enviado! Avisaremos quando aprovarmos.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background">
        <SEO title="Cadastro recebido" description="Cadastro de parceiro CareOS recebido." noIndex />
        <div className="container max-w-xl mx-auto p-4 space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-success" />
                <CardTitle>Cadastro recebido</CardTitle>
              </div>
              <CardDescription>
                Vamos analisar suas informações e te avisar por e-mail. Após aprovação,
                entre no painel com o mesmo e-mail (<strong>{form.email}</strong>) para gerar seu link.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-md border bg-muted/50 p-3 text-sm">
                Seu código de indicação: <strong>{done.code}</strong>
              </div>
              <Button onClick={() => navigate("/parceiros")} className="w-full sm:w-auto">
                Voltar
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Cadastro de parceiro CareOS"
        description="Cadastre-se no programa de parcerias do CareOS e receba comissão por indicações."
        canonical="https://careos.life/parceiros/cadastro"
      />
      <div className="container max-w-xl mx-auto p-4 space-y-4">
        <Link to="/parceiros" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </Link>

        <Card>
          <CardHeader>
            <CardTitle>Quero ser parceiro CareOS</CardTitle>
            <CardDescription>
              Preencha seus dados. Avaliamos manualmente em até 2 dias úteis.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome ou razão social *</Label>
                <Input id="name" required value={form.name} onChange={(e) => set("name", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-mail *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  disabled={!!authedEmail}
                  readOnly={!!authedEmail}
                />
                {authedEmail && (
                  <p className="text-xs text-muted-foreground">
                    Vinculado à sua conta. Para usar outro e-mail, saia e cadastre sem login.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">WhatsApp / telefone</Label>
                <Input id="phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="partner_type">Tipo de parceiro</Label>
                <Select value={form.partner_type} onValueChange={(v) => set("partner_type", v)}>
                  <SelectTrigger id="partner_type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="document">CNPJ ou CPF</Label>
                <Input id="document" value={form.document} onChange={(e) => set("document", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Conte sobre seu público</Label>
                <Textarea id="notes" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
              </div>
              <p className="text-xs text-muted-foreground">
                Seu código de indicação será gerado automaticamente após o envio.
              </p>
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? "Enviando..." : "Enviar cadastro"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
